/** Individual product result card — supports grid and list view modes. */

import { h } from 'preact';
import { useState, useRef, useEffect } from 'preact/hooks';
import { getDisplayPriceParts, getProductUrl, trackClick, prefetchProductPage } from '../api';
import type { ProductResult } from '../api';
import { getConfig } from '../config';

export type ViewMode = 'grid' | 'list';
const PREFETCH_HOVER_MS = 150;

function normalizeDescriptionLine(line: string): string {
  const cleaned = line
    .replace(/^[•▪◦\-–—\s]+/, '')
    .replace(/\s+/g, ' ')
    .trim();

  if (!cleaned) return '';
  if (/^available\b.*\biso\b/i.test(cleaned)) return '';
  if (cleaned.length < 12 && !cleaned.endsWith(':')) return '';
  if (/[.!?]$/.test(cleaned) || cleaned.endsWith(':')) return cleaned;
  return `${cleaned}.`;
}

function extractListDescriptionItems(description: string): string[] {
  return description
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*<p[^>]*>/gi, '\n')
    .replace(/<\/li>\s*<li[^>]*>/gi, '\n')
    .replace(/<li[^>]*>/gi, '')
    .replace(/<\/li>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, '\'')
    .replace(/&bull;|&#8226;/gi, '\n')
    .replace(/[•▪◦]/g, '\n')
    .replace(/\r\n?/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .split('\n')
    .map(normalizeDescriptionLine)
    .filter(Boolean)
    .slice(0, 3);
}

function normalizeDisplayBullets(bullets?: string[]): string[] {
  if (!bullets || bullets.length === 0) return [];
  return bullets
    .map((line) => normalizeDescriptionLine(line))
    .filter(Boolean)
    .slice(0, 3);
}

function _formatIsoGradeRange(grades: number[]): string | null {
  const sorted = [...grades].filter((g) => Number.isFinite(g)).sort((a, b) => a - b);
  if (sorted.length === 0) return null;
  if (sorted.length === 1) return `ISO ${sorted[0]}`;
  const allIntegers = sorted.every((g) => Number.isInteger(g));
  const isContiguous = allIntegers && sorted.every((g, i) => i === 0 || g === sorted[i - 1] + 1);
  if (isContiguous) return `ISO ${sorted[0]}-${sorted[sorted.length - 1]}`;
  return `ISO ${sorted.map((g) => String(g)).join(', ')}`;
}

function extractIsoGradeSummary(product: ProductResult): string | null {
  // Tier 1: structured iso_classes field from A4 productAttributesValues.
  // Each entry looks like "ISO 5", "ISO 6", etc.  Single source of truth
  // when populated — covers ~7,700 of the 16,300 active products.
  const struct = product.iso_classes;
  if (struct && struct.length > 0) {
    const nums = struct
      .map((s) => {
        const m = String(s).match(/(\d(?:\.\d+)?)/);
        return m ? Number(m[1]) : NaN;
      })
      .filter((n) => Number.isFinite(n));
    const summary = _formatIsoGradeRange(nums);
    if (summary) return summary;
  }

  // Tier 2 fallback: regex-extract from description bullets for products
  // whose A4 record predates the structured attribute (legacy data).
  const description = product.description || '';
  const matches = Array.from(description.matchAll(/\bISO(?:\s+Class)?\s*(\d(?:\.\d+)?)\b/gi));
  if (matches.length === 0) return null;
  const unique = Array.from(new Set(matches.map((m) => m[1]))).map(Number);
  return _formatIsoGradeRange(unique);
}

function extractSpecChips(isoGradeSummary: string | null): string[] {
  return isoGradeSummary ? [isoGradeSummary] : [];
}

function ResultNameText({ text }: { text: string }) {
  return <span>{text}</span>;
}

function PriceText({ prefix, amount }: { prefix?: string; amount: string }) {
  return (
    <span class="ts-price-text">
      {prefix && <span class="ts-price-prefix">{prefix}</span>}
      <span class="ts-price-amount">{amount}</span>
    </span>
  );
}

/** Reject obvious garbage in the lead_time_text field before showing it
 *  to a customer.  A4's `lt_days_quote` / `lt_buy_quote` source data has
 *  ~10% non-actionable values (zero ranges, placeholders, absurd
 *  durations).  Rendering "0-0 days" or "TBD" looks like a software bug
 *  to a buyer; better to render no lead-time badge at all than surface
 *  misinformation.
 *
 *  Drops:
 *    - empty / "0" / "0 days" / "0-0" / "0-N" zero-floor ranges
 *    - placeholder tokens ("tbd", "tba", "quote", "?", "—", "varies")
 *    - durations >180 days that aren't expressed as months/weeks (raw
 *      "365 days" is almost always a sentinel rather than a real ETA)
 *
 *  Accepts:
 *    - "5-7 days", "10-15 days", "10 weeks", "3-5 months", etc.
 *    - "1 day", "2 weeks"
 */
function isLogicalLeadTime(raw: string): boolean {
  const s = raw.trim().toLowerCase();
  if (!s) return false;
  if (/\b(tbd|tba|n\/a|na|quote|varies|unknown|placeholder)\b/.test(s)) return false;
  if (/[?<>—–]/.test(s)) return false;
  // Pure-zero or zero-floor range: "0", "0 days", "0-0", "0 - 5"
  if (/^0\s*(?:days?|weeks?|months?)?$/.test(s)) return false;
  if (/^0\s*[-–]\s*\d+/.test(s)) return false;
  // Absurd raw-day counts (>180 with "day" unit only — months/weeks ok).
  const dayMatch = s.match(/^(\d{1,4})\s*(?:days?)?$/);
  if (dayMatch) {
    const n = parseInt(dayMatch[1], 10);
    if (!Number.isFinite(n) || n <= 0 || n > 180) return false;
  }
  return true;
}


interface ResultCardProps {
  product: ProductResult;
  query: string;
  position: number;
  viewMode: ViewMode;
}

export function ResultCard({
  product,
  query,
  position,
  viewMode,
}: ResultCardProps) {
  const [imgError, setImgError] = useState(false);
  const productUrl = getProductUrl(product);
  const priceParts = getDisplayPriceParts(product);
  const isQuoteOnly = !!priceParts?.isQuote;
  const displayBullets = normalizeDisplayBullets(product.display_bullets);
  const descriptionItems = displayBullets.length > 0
    ? displayBullets
    : (product.description ? extractListDescriptionItems(product.description) : []);
  const isoGradeSummary = extractIsoGradeSummary(product);
  const specChips = extractSpecChips(isoGradeSummary);
  const hoverTimerRef = useRef<number | null>(null);

  // Prefetch tiers (defense in depth against perceived navigation latency):
  //   - position < 4: eager — top of list, very likely first click target
  //   - position >= 4: requestIdleCallback (or 200ms setTimeout fallback) —
  //                    fire when the main thread is idle so we don't compete
  //                    with first paint or smooth scroll
  //   - hover (handleHoverStart below): primary intent signal, fires after a
  //                    150ms debounce regardless of position
  // prefetchProductPage is idempotent (Set-guarded), so duplicate calls are free.
  useEffect(() => {
    if (!productUrl) return;
    if (position < 4) {
      prefetchProductPage(productUrl);
      return;
    }
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    const ric = w.requestIdleCallback
      ? (cb: () => void) => w.requestIdleCallback!(cb, { timeout: 2000 })
      : (cb: () => void) => window.setTimeout(cb, 200);
    const cic = w.cancelIdleCallback
      ? (id: number) => w.cancelIdleCallback!(id)
      : (id: number) => window.clearTimeout(id);
    const handle = ric(() => prefetchProductPage(productUrl));
    return () => cic(handle);
  }, [productUrl, position]);
  // Lead-time label, sourced from A4 Inventory_Messaging:
  //   1. `lead_time_text` — substituted from <LTDaysQuote>/<LTBuyQuote>
  //      tokens in the rule template (e.g. "5-7 days", "10-12 weeks").
  //   2. `lead_time_days` — legacy integer field, only present on
  //      products whose A4 record has a numeric LTDays override.
  // Validated against `isLogicalLeadTime()` so junk values from A4
  // (e.g. "0 days", "0-0", "TBD", placeholders, absurd ranges) are
  // dropped before display rather than rendered as misinformation.
  const leadTimeLabel = (() => {
    const text = product.lead_time_text?.trim();
    if (text && isLogicalLeadTime(text)) return text;
    const days = product.lead_time_days;
    if (days != null && days > 0 && days <= 365) {
      return `Est. lead time ${days} day${days !== 1 ? 's' : ''}`;
    }
    return null;
  })();

  const handleHoverStart = () => {
    if (!productUrl) return;
    hoverTimerRef.current = window.setTimeout(() => prefetchProductPage(productUrl), PREFETCH_HOVER_MS);
  };
  const handleHoverEnd = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  };

  const handleClick = () => {
    trackClick(query, product.product_id, position);
    window.dataLayer?.push({
      event: 'select_item',
      ecommerce: {
        items: [
          {
            item_name: product.name,
            item_id: product.part_number,
            price: product.price,
            item_category: product.category,
            item_category2: product.parent_product_line || product.product_line,
            currency: 'USD',
            index: position,
          },
        ],
      },
    });
  };

  // Build breadcrumb: Category > Parent Product Name (or product_line fallback)
  // Suppress junk catch-all categories from display
  const JUNK_CATEGORIES = ['zee other stuff', 'zee other', 'other stuff'];
  const rawCategory = product.category || '';
  const isJunkCategory = JUNK_CATEGORIES.includes(rawCategory.toLowerCase());
  const categoryName = isJunkCategory ? '' : rawCategory;
  const categoryUrl = isJunkCategory ? '' : (product.category_url || '');
  const parentName = product.parent_product_name || '';
  const parentUrl = product.parent_product_url || '';
  const secondaryName = parentName || product.product_line || '';
  const secondaryUrl = parentUrl;  // only parent products have URLs
  // Same Part # badge for parents and non-parents.  Parents show their
  // -PP- part number; the "more products" prominence belongs at the
  // bottom of the card list (the results-page CTA), not duplicated as
  // a per-card badge.
  const partNumberBadge = product.part_number ? (
    <span class="ts-result-card-partnum" aria-label={`Part number ${product.part_number}`}>
      <span class="ts-result-card-partnum-label">Part #</span>
      <span class="ts-result-card-partnum-value">{product.part_number}</span>
    </span>
  ) : null;
  // Services (part numbers starting with 9999) shouldn't show stock badges
  const isService = product.part_number?.startsWith('9999');
  // Availability badge — only two visible signals:
  //   1. In Stock — `availability_status='in_stock'` or legacy
  //      `stocked` boolean (Qty_On_Hand>0).
  //   2. Lead-time — when the product is made-to-order AND we have a
  //      logical lead-time string from A4.  Junk values are filtered
  //      by `isLogicalLeadTime()` upstream so we don't render
  //      misinformation.
  // No "In Stock Soon" / "Low Stock" / "Made to Order" filler — A4's
  // signals for those are inconsistently populated and the resulting
  // badges did more harm than good in stakeholder review.
  const cfg = getConfig();
  let availabilityBadge: h.JSX.Element | null = null;
  if (!isService) {
    const status = (product.availability_status || '').toLowerCase();
    if ((status === 'in_stock' || product.stocked) && cfg.showInStockBadge) {
      const message = (product.availability_message || '').trim();
      availabilityBadge = (
        <span class="ts-badge ts-badge--in-stock">{message || 'In Stock'}</span>
      );
    } else if (leadTimeLabel && cfg.showLeadTime) {
      availabilityBadge = (
        <span class="ts-badge ts-badge--lead-time">{leadTimeLabel}</span>
      );
    }
  }
  const imageBlock = (
    <a
      href={productUrl}
      class={`ts-result-card-image-link ${viewMode === 'list' ? 'ts-result-card-image-link--list' : ''}`}
      onClick={handleClick}
      target="_blank"
      rel="noopener noreferrer"
    >
      {product.image_url && !imgError ? (
        <img
          src={product.image_url}
          alt={product.name}
          class="ts-result-card-image"
          loading={position < 4 ? 'eager' : 'lazy'}
          decoding="async"
          fetchpriority={position < 2 ? 'high' : 'auto'}
          onError={(e) => { console.error('[TerraSearch] Image load failed:', product.image_url, e); setImgError(true); }}
        />
      ) : (
        <div class="ts-result-card-image-placeholder">
          <span class="ts-result-card-image-initials">
            {product.name
              .split(/[\s,]+/)
              .slice(0, 2)
              .map((w) => (w[0] || '').toUpperCase())
              .join('')}
          </span>
          {product.category && (
            <span class="ts-result-card-image-category">
              {product.category.split(' ')[0]}
            </span>
          )}
        </div>
      )}
    </a>
  );

  if (viewMode === 'list') {
    return (
      <div class="ts-result-card ts-result-card--list" onMouseEnter={handleHoverStart} onMouseLeave={handleHoverEnd}>
        {imageBlock}
        <div class="ts-result-card-body ts-result-card-body--list">
          <div class="ts-result-card-content">
            <div class="ts-result-card-copy">
              {(categoryName || secondaryName) && (
                <p class="ts-result-card-breadcrumb">
                  {categoryUrl ? (
                    <a href={categoryUrl} class="ts-breadcrumb-link" target="_blank" rel="noopener noreferrer">{categoryName}</a>
                  ) : (
                    categoryName
                  )}
                  {categoryName && secondaryName && (
                    <span class="ts-breadcrumb-sep" aria-hidden="true">{' / '}</span>
                  )}
                  {secondaryUrl ? (
                    <a href={secondaryUrl} class="ts-breadcrumb-link ts-breadcrumb-link--parent" target="_blank" rel="noopener noreferrer">{secondaryName}</a>
                  ) : (
                    secondaryName
                  )}
                </p>
              )}
              <div class="ts-result-card-name-wrap">
                <a href={productUrl} class="ts-result-card-name" onClick={handleClick} target="_blank" rel="noopener noreferrer">
                  <ResultNameText text={product.name} />
                </a>
              </div>
              <div class="ts-result-card-meta">
                {partNumberBadge}
                {availabilityBadge}
              </div>
            </div>
            {specChips.length > 0 && (
              <div class="ts-result-specs" aria-label="Key specifications">
                {specChips.map((spec) => (
                  <span class="ts-result-spec-chip" key={`${product.product_id}-${spec}`}>{spec}</span>
                ))}
              </div>
            )}
            {descriptionItems.length > 0 && (
              <ul class="ts-result-card-desc-list">
                {descriptionItems.map((line, index) => (
                  <li class="ts-result-card-desc-item" key={`${product.product_id}-${index}`}>{line}</li>
                ))}
              </ul>
            )}
          </div>
          {(priceParts || productUrl) && (
            <div class="ts-result-card-action-rail">
              {priceParts && (
                <span class={`ts-result-card-price ${isQuoteOnly ? 'ts-result-card-price--quote' : ''}`}>
                  {isQuoteOnly ? priceParts.amount : <PriceText prefix={priceParts.prefix} amount={priceParts.amount} />}
                </span>
              )}
              <div class="ts-result-card-actions">
                {productUrl && (
                  <a href={productUrl} class="ts-result-card-view-btn" onClick={handleClick} target="_blank" rel="noopener noreferrer">
                    View Product
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Grid view (default)
  return (
    <div class="ts-result-card" onMouseEnter={handleHoverStart} onMouseLeave={handleHoverEnd}>
      {imageBlock}
      <div class="ts-result-card-body">
        {(categoryName || secondaryName) && (
          <p class="ts-result-card-breadcrumb">
            {categoryUrl ? (
              <a href={categoryUrl} class="ts-breadcrumb-link" target="_blank" rel="noopener noreferrer">{categoryName}</a>
            ) : (
              categoryName
            )}
            {categoryName && secondaryName && (
                    <span class="ts-breadcrumb-sep" aria-hidden="true">{' / '}</span>
                  )}
            {secondaryUrl ? (
              <a href={secondaryUrl} class="ts-breadcrumb-link ts-breadcrumb-link--parent" target="_blank" rel="noopener noreferrer">{secondaryName}</a>
            ) : (
              secondaryName
            )}
          </p>
        )}
        <div class="ts-result-card-name-wrap">
          <a href={productUrl} class="ts-result-card-name" onClick={handleClick} target="_blank" rel="noopener noreferrer">
            <ResultNameText text={product.name} />
          </a>
        </div>
        <div class="ts-result-card-meta">
          {partNumberBadge}
          {availabilityBadge}
        </div>
        {specChips.length > 0 && (
          <div class="ts-result-specs" aria-label="Key specifications">
            {specChips.slice(0, 4).map((spec) => (
              <span class="ts-result-spec-chip" key={`${product.product_id}-${spec}`}>{spec}</span>
            ))}
          </div>
        )}
        <div class="ts-result-card-price-row">
          {priceParts && (
            <span class={`ts-result-card-price ${isQuoteOnly ? 'ts-result-card-price--quote' : ''}`}>
              {isQuoteOnly ? priceParts.amount : <PriceText prefix={priceParts.prefix} amount={priceParts.amount} />}
            </span>
          )}
          <div class="ts-result-card-grid-actions">
            {productUrl && (
              <a href={productUrl} class="ts-result-card-view-btn ts-result-card-view-btn--grid" onClick={handleClick} target="_blank" rel="noopener noreferrer">
                View
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function SkeletonCard({ viewMode = 'grid' }: { viewMode?: ViewMode }) {
  if (viewMode === 'list') {
    return (
      <div class="ts-result-card ts-result-card--list ts-result-card--skeleton">
        <div class="ts-skeleton ts-skeleton--image-list" />
        <div class="ts-result-card-body ts-result-card-body--list">
          <div class="ts-result-card-content">
            <div class="ts-skeleton ts-skeleton--text ts-skeleton--w40" />
            <div class="ts-skeleton ts-skeleton--text ts-skeleton--w80" />
            <div class="ts-skeleton ts-skeleton--text ts-skeleton--w60" />
            <div class="ts-skeleton ts-skeleton--text ts-skeleton--w90" />
            <div class="ts-skeleton ts-skeleton--text ts-skeleton--w85" />
            <div class="ts-skeleton ts-skeleton--text ts-skeleton--w75" />
          </div>
          <div class="ts-result-card-action-rail">
            <div class="ts-skeleton ts-skeleton--text ts-skeleton--w70" />
            <div class="ts-skeleton ts-skeleton--text ts-skeleton--w90" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div class="ts-result-card ts-result-card--skeleton">
      <div class="ts-skeleton ts-skeleton--image" />
      <div class="ts-result-card-body">
        <div class="ts-skeleton ts-skeleton--text ts-skeleton--w60" />
        <div class="ts-skeleton ts-skeleton--text ts-skeleton--w80" />
        <div class="ts-skeleton ts-skeleton--text ts-skeleton--w40" />
        <div class="ts-skeleton ts-skeleton--text ts-skeleton--w30" />
      </div>
    </div>
  );
}
